import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Card, cx } from "@/components/ui";
import { api, type ChannelDetail } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";

import { t } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
/** Aufträge, die Shopify nicht an Logiful übergeben lässt (z. B. zurückgestellt) – mit Link in den Shop */
export function RefusedRequestsCard({ items }: { items: NonNullable<ChannelDetail["refusedRequests"]> }) {
  if (!items.length) return null;
  return (
    <Card className="p-5 border-warning/30">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><CircleAlert className="size-4 text-warning" /> {items.length === 1 ? t("1 Auftrag") : t("{length} Aufträge", { length: items.length })} {t("lässt Shopify nicht an Logiful übergeben")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Meist ist der Auftrag in Shopify zurückgestellt, für später geplant oder bereits geschlossen. Das Portal versucht es stündlich erneut – nach einer Änderung in Shopify kommt er automatisch.")}</p>
      <ul className="mt-3 rounded-xl border border-border divide-y divide-border text-[13px]">
        {items.map((r) => (
          <li key={r.fulfillmentOrderId} className="flex items-center gap-3 px-3 py-2">
            <span className="font-medium tabular-nums">{r.orderNumber}</span>
            <span className="min-w-0 flex-1 truncate text-fg-muted" title={serverText(r.reason)}>{serverText(r.reason)}</span>
            <span className="text-[12px] text-fg-subtle shrink-0">{fmtRelative(r.at)}</span>
            <a href={r.shopUrl} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium hover:underline">{t("In Shopify öffnen")} <ExternalLink className="size-3" /></a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Varianten ohne SKU in Shopify: werden nicht an der Logiful-Location geführt → Bestellungen gehen nicht an Logiful */
export function MissingSkuCard({ channelId, missing, fill, editable }: { channelId: string; missing: NonNullable<ChannelDetail["missingSku"]>; fill: boolean; editable: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.channels.setFillMissingSku(tenantId, channelId, enabled),
    onSuccess: (_r, enabled) => {
      toast.success("Gespeichert", { description: enabled ? "Fehlende SKUs werden in den nächsten Minuten in Shopify ergänzt." : undefined });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const on = save.isPending ? !!save.variables : fill;
  if (!missing.count && !fill) return null;
  return (
    <Card className={cx("p-5", missing.count > 0 && "border-warning/30")}>
      <h2 className="font-semibold text-[14px] flex items-center gap-2">
        {missing.count > 0 && <CircleAlert className="size-4 text-warning" />}
        {missing.count > 0 ? t("{v} Variante{v2} ohne SKU in Shopify", { v: fmtNumber(missing.count), v2: missing.count === 1 ? "" : "n" }) : t("SKUs in Shopify")}
      </h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        {t("Shopify führt Varianten ohne SKU nicht an der Logiful-Location. Bestellungen mit diesen Artikeln gehen dann an einen anderen Standort statt an Logiful (Split).")}
      </p>
      <label className={cx("mt-3 flex items-start gap-2.5 rounded-xl border p-3", on ? "border-ink bg-surface-2" : "border-border", editable ? "cursor-pointer" : "cursor-default")}>
        <input type="checkbox" className="mt-1 accent-ink" checked={on} disabled={!editable} onChange={(e) => save.mutate(e.target.checked)} />
        <span>
          <span className="block text-[13px] font-medium">{t("Fehlende SKU automatisch in Shopify ergänzen")}</span>
          <span className="block text-[12px] text-fg-muted">{t("Das Portal trägt die SKU aus dem Portal (bei Varianten ohne SKU die Varianten-ID) in Shopify ein und führt die Variante danach an der Logiful-Location. Braucht die Berechtigung write_products.")}</span>
        </span>
      </label>
      {missing.items.length > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)} className="mt-2 text-[12.5px] font-medium underline underline-offset-2">{open ? t("Liste ausblenden") : t("Betroffene Artikel anzeigen")}</button>
          {open && (
            <ul className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border text-[13px]">
              {missing.items.map((m) => (
                <li key={m.productId} className="flex items-center gap-3 px-3 py-1.5">
                  <Link to={`/products?open=${m.productId}`} className="min-w-0 flex-1 truncate hover:underline">{m.name}</Link>
                  <span className="font-mono text-[11.5px] text-fg-muted">{m.sku}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
